/**
 * listeningTimeBuffer — localStorage-backed offline buffer for listening time payloads.
 *
 * When a track() call fails (network error, timeout, degraded connection), the caller
 * pushes the payload here. On reconnect (window 'online' event), the caller drains
 * the buffer by calling getAndClear() and re-submitting the entries.
 *
 * Storage key: 'scribelate-lt-buffer'
 * Format: JSON array of BufferedListeningTimeEntry
 *
 * Security note: only stores seconds counts, language codes, and media type — no PII,
 * no auth tokens. Safe to persist in localStorage.
 */

export interface BufferedListeningTimeEntry {
  seconds: number;
  language: string;
  media_type?: string;
  /** ISO timestamp of when the entry was buffered — for debugging only, not sent. */
  bufferedAt: string;
}

const STORAGE_KEY = 'scribelate-lt-buffer';
const MAX_ENTRIES = 100; // safety cap — ~50 hours of 30s batches

function readBuffer(): BufferedListeningTimeEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeBuffer(entries: BufferedListeningTimeEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage unavailable (private browsing quota, etc.) — fail silently
  }
}

/** Add a failed payload to the buffer. */
export function bufferListeningTime(
  payload: Pick<BufferedListeningTimeEntry, 'seconds' | 'language' | 'media_type'>,
): void {
  const entries = readBuffer();
  entries.push({ ...payload, bufferedAt: new Date().toISOString() });
  // Enforce cap: drop oldest entries if needed
  const capped = entries.length > MAX_ENTRIES ? entries.slice(-MAX_ENTRIES) : entries;
  writeBuffer(capped);
}

/** Read all buffered entries and clear the buffer atomically. Returns [] if empty. */
export function getAndClearBuffer(): BufferedListeningTimeEntry[] {
  const entries = readBuffer();
  if (entries.length > 0) {
    writeBuffer([]);
  }
  return entries;
}

/** True if there are buffered entries waiting to be sent. */
export function hasBufferedEntries(): boolean {
  return readBuffer().length > 0;
}
